import React, { useRef, useState, useEffect } from "react";
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, notification, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./modal_data_transfer_accept.css";
import axios from "axios";
import useSWR from "swr";

interface DataType {
  key: string;
  requestNo: string;
  requestDate: string;
  requestBy: string;
  requestByName: string;
  fromWhName: string;
  fromWhCode: string;
  toWhName: string;
  toWhCode: string;
  purpose: string;
  approval: string;
  approvalByName: string;
  approvalDate: string;
  approvalRemark: string;
}

interface ApiResponse {
  data: DataType[];
  total: number;
}

interface ModalDataTransferAcceptProps {
  onRowDoubleClick: (record: DataType) => void;
  userWarehouseCode: string | undefined;
}

type DataIndex = keyof DataType;
const fetcher = (url: string) => axios.get(url).then(res => res.data);

const ModalDataTransferAccept: React.FC<ModalDataTransferAcceptProps> = ({ onRowDoubleClick, userWarehouseCode }) => {
  const transformStyle = {
    transform: "scale(0.80)",
    transformOrigin: "top left",
    width: "125%",
  };

  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<InputRef>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [sort, setSort] = useState<{ field?: string, order?: string }>({});
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [tableData, setTableData] = useState<DataType[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const buildQueryString = () => {
    const params = new URLSearchParams();
    params.append('page', pagination.current.toString());
    params.append('pageSize', pagination.pageSize.toString());
    if (sort.field) {
      params.append('sortField', sort.field);
      params.append('sortOrder', sort.order || '');
    }
    Object.entries(filters).forEach(([key, val]) => {
      if (val) params.append(key, val);
    });
    return params.toString();
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get<ApiResponse>(`${process.env.REACT_APP_BACKEND_URL}/api/transfer-requests-accept?${buildQueryString()}`);
      if (response.data && Array.isArray(response.data.data)) {

        setTableData(response.data.data);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error("Connection drops or RTO during modal data load:", err);
      notification.error({
        message: "Koneksi Terganggu (RTO)",
        description: "Tidak dapat menyelaraskan data dengan server. Silakan muat ulang.",
        placement: "topRight"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination.current, pagination.pageSize, sort, filters]);

  const handleRowSelect = (record: DataType) => {
    if (record.fromWhCode !== userWarehouseCode) {
      notification.warning({
        message: 'Access Denied',
        description: 'cannot select those that are not from the original warehouse',
        placement: 'topRight',
      });
    } else {
      onRowDoubleClick(record);
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };


  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: DataIndex,
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
    setFilters(prev => ({ ...prev, [dataIndex]: selectedKeys[0] }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
    clearFilters();
    setSearchText('');
    setFilters(prev => {
      const updated = { ...prev };
      delete updated[dataIndex];
      return updated;
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const columns: TableColumnsType<DataType> = [
    {
      title: 'Request No',
      dataIndex: 'requestNo',
      key: 'requestNo',
      ...getColumnSearchProps('requestNo'),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Request Date',
      dataIndex: 'requestDate',
      key: 'requestDate',
      ...getColumnSearchProps('requestDate'),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (text: string) => formatDate(text),
    },
    {
      title: 'Request By',
      dataIndex: 'requestByName',
      key: 'requestByName',
      ...getColumnSearchProps('requestByName'),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'From Wh Name',
      dataIndex: 'fromWhName',
      key: 'fromWhName',
      ...getColumnSearchProps('fromWhName'),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'From Wh Code',
      dataIndex: 'fromWhCode',
      key: 'fromWhCode',
      ...getColumnSearchProps('fromWhCode'),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
  ];

  return (

    <div id="iiok" style={transformStyle}>
      <div className="gjs-grid-row" id="i8mm">
        <div className="gjs-grid-column" id="ipnh">
          <Table<DataType>
            columns={columns}
            dataSource={tableData}
            loading={isLoading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: total, 
              showSizeChanger: true,
            }}
            onChange={(newPagination, _, newSorter) => {
              setPagination({
                current: newPagination.current || 1,
                pageSize: newPagination.pageSize || 10,
              });
              if (!Array.isArray(newSorter) && newSorter.field) {
                setSort({
                  field: newSorter.field as string,
                  order: newSorter.order || undefined,
                });
              } else {
                setSort({});
              }
            }}
            onRow={(record) => {
              return {
                onDoubleClick: () => handleRowSelect(record),
              };
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ModalDataTransferAccept;